import React, { useEffect, useMemo, useState } from 'react';
import { emailNotificationApi } from '../services/api';
import { PageHead, Card, Btn, Spinner, Toast } from '../components/UI';

export default function EmailNotificationPage() {
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    load();
  }, []);

  async function load() {
    setLoading(true);
    try {
      const res = await emailNotificationApi.getAll();
      setConfigs((res.data?.data || res.data || []).map(normalizeConfig));
    } catch (err) {
      setConfigs([]);
      setToast({ msg: getErrorMessage(err, 'Failed to load email settings.'), type: 'error' });
    } finally {
      setLoading(false);
    }
  }

  function updateFieldByIndex(index, key, value) {
    setConfigs((prev) =>
      prev.map((c, i) => (i === index ? { ...c, [key]: value } : c))
    );
  }

  const hasRows = useMemo(() => configs.length > 0, [configs]);

  async function saveAll() {
    setSaving(true);
    try {
      await emailNotificationApi.saveAll(
        configs.map((c) => ({
          campusID: c.campusID,
          requestTo: c.requestTo || '',
          requestCc: c.requestCc || '',
          ccRequesterOnRequest: !!c.ccRequesterOnRequest,
          approvedTo: c.approvedTo || '',
          approvedCc: c.approvedCc || '',
          ccRequesterOnApproved: !!c.ccRequesterOnApproved,
          rejectedTo: c.rejectedTo || '',
          rejectedCc: c.rejectedCc || '',
          ccRequesterOnRejected: !!c.ccRequesterOnRejected,
        }))
      );
      setToast({ msg: 'Email notification settings saved.', type: 'success' });
      await load();
    } catch (err) {
      setToast({ msg: getErrorMessage(err, 'Failed to save email settings.'), type: 'error' });
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '4rem', gap: 10 }}>
        <Spinner size={24} />
        <span style={{ color: 'var(--text2)' }}>Loading email notification settings...</span>
      </div>
    );
  }

  return (
    <div className="page-anim">
      <PageHead
        title="Email Notification Config"
        subtitle="Campus-wise notification routing for request, approval, and rejection"
        actions={
          <Btn variant="primary" onClick={saveAll} disabled={saving || !hasRows}>
            {saving ? <Spinner size={16} color="white" /> : 'Save Email Configuration'}
          </Btn>
        }
      />

      <Card style={{ marginBottom: '1rem', background: 'var(--orange-light)' }}>
        <div style={{ fontSize: 13, color: 'var(--text2)', lineHeight: 1.55 }}>
          Enter multiple emails separated by commas. After approve/reject, the person who booked is always notified.
          You can additionally keep them in CC with the toggle.
        </div>
      </Card>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        {configs.map((cfg, idx) => (
          <Card key={`${cfg.campusID ?? 'campus'}-${idx}`}>
            <div style={{ fontFamily: 'var(--font-head)', fontSize: 17, fontWeight: 700, marginBottom: '1rem' }}>
              {cfg.campusName}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,minmax(0,1fr))', gap: '1rem' }} className="email-grid">
              <Section
                  title="Booking LE Request"
                to={cfg.requestTo}
                cc={cfg.requestCc}
                toggle={cfg.ccRequesterOnRequest}
                onTo={(v) => updateFieldByIndex(idx, 'requestTo', v)}
                onCc={(v) => updateFieldByIndex(idx, 'requestCc', v)}
                onToggle={(v) => updateFieldByIndex(idx, 'ccRequesterOnRequest', v)}
              />
              <Section
                title="After Approval"
                to={cfg.approvedTo}
                cc={cfg.approvedCc}
                toggle={cfg.ccRequesterOnApproved}
                onTo={(v) => updateFieldByIndex(idx, 'approvedTo', v)}
                onCc={(v) => updateFieldByIndex(idx, 'approvedCc', v)}
                onToggle={(v) => updateFieldByIndex(idx, 'ccRequesterOnApproved', v)}
              />
              <Section
                title="After Rejection"
                to={cfg.rejectedTo}
                cc={cfg.rejectedCc}
                toggle={cfg.ccRequesterOnRejected}
                onTo={(v) => updateFieldByIndex(idx, 'rejectedTo', v)}
                onCc={(v) => updateFieldByIndex(idx, 'rejectedCc', v)}
                onToggle={(v) => updateFieldByIndex(idx, 'ccRequesterOnRejected', v)}
              />
            </div>
          </Card>
        ))}
      </div>

      <style>{`
        @media(max-width:980px){
          .email-grid{grid-template-columns:1fr!important}
        }
      `}</style>

      {toast && <Toast msg={toast.msg} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
}

function Section({ title, to, cc, toggle, onTo, onCc, onToggle }) {
  const textareaStyle = {
    width: '100%',
    minHeight: 90,
    padding: '10px 12px',
    border: '1.5px solid var(--border)',
    borderRadius: 8,
    fontSize: 14,
    fontFamily: 'var(--font-body)',
    resize: 'vertical',
    outline: 'none',
    background: 'white',
    color: 'var(--text1)',
  };

  return (
    <div style={{ border: '1.5px solid var(--border)', borderRadius: 12, padding: '0.9rem' }}>
      <div style={{ fontSize: 13, fontWeight: 700, marginBottom: '0.7rem' }}>{title}</div>

      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text2)', textTransform: 'uppercase', marginBottom: 6 }}>To Emails</div>
      <textarea
        value={to}
        onChange={(e) => onTo(e.target.value)}
        onInput={(e) => onTo(e.currentTarget.value)}
        placeholder="management@elproschools.edu.in, principal@elproschools.edu.in"
        readOnly={false}
        spellCheck={false}
        style={textareaStyle}
      />

      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text2)', textTransform: 'uppercase', margin: '10px 0 6px' }}>CC Emails</div>
      <textarea
        value={cc}
        onChange={(e) => onCc(e.target.value)}
        onInput={(e) => onCc(e.currentTarget.value)}
        placeholder="optional.cc@elproschools.edu.in"
        readOnly={false}
        spellCheck={false}
        style={textareaStyle}
      />

      <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10, fontSize: 12, color: 'var(--text2)', cursor: 'pointer' }}>
        <input type="checkbox" checked={!!toggle} onChange={(e) => onToggle(e.target.checked)} />
        CC who booked
      </label>
    </div>
  );
}

function getErrorMessage(err, fallback) {
  if (err?.response?.status === 403) {
    return 'Admin permission required. Please logout and login again with an Admin account.';
  }
  return err?.response?.data?.message || fallback;
}

function normalizeConfig(c) {
  const campusID = c.campusID ?? c.campusId ?? c.CampusID ?? c.CampusId ?? 0;
  return {
    campusID,
    campusName: c.campusName || c.CampusName || '',
    requestTo: c.requestTo ?? c.RequestTo ?? '',
    requestCc: c.requestCc ?? c.RequestCc ?? '',
    ccRequesterOnRequest: (c.ccRequesterOnRequest ?? c.CcRequesterOnRequest) !== false,
    approvedTo: c.approvedTo ?? c.ApprovedTo ?? '',
    approvedCc: c.approvedCc ?? c.ApprovedCc ?? '',
    ccRequesterOnApproved: (c.ccRequesterOnApproved ?? c.CcRequesterOnApproved) !== false,
    rejectedTo: c.rejectedTo ?? c.RejectedTo ?? '',
    rejectedCc: c.rejectedCc ?? c.RejectedCc ?? '',
    ccRequesterOnRejected: (c.ccRequesterOnRejected ?? c.CcRequesterOnRejected) !== false,
  };
}
